/**
 * ⌘K command palette.
 *
 * Opens via Cmd/Ctrl+K from anywhere. Pluggable via `useCommands(provider)`
 * — pages register their commands and the palette aggregates them.
 *
 * R0: shell only — empty providers + a few static "go to" commands so the
 * palette has something useful from day one. Real pages register their own
 * commands in R1+ (e.g., on the Customer page: "Issue invoice", "Suspend
 * subscription"; on the Inbox page: "Mark thread as resolved", etc.).
 */

import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { create } from 'zustand'
import { useUiStore } from '@/store/ui'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { Icon, type IconVariant } from '@/components/ui/icon'

export interface CommandItem {
  id: string
  label: string
  hint?: string
  group?: string
  /** FA glyph name (no `fa-` prefix). */
  icon?: string
  iconVariant?: IconVariant
  perm?: string
  shortcut?: string
  run: () => void
}

interface CommandRegistryState {
  byProvider: Record<string, CommandItem[]>
  register: (provider: string, items: CommandItem[]) => void
  unregister: (provider: string) => void
  all: () => CommandItem[]
}

/** Singleton registry. Pages call useCommands(providerId, items) to register. */
export const useCommandRegistry = create<CommandRegistryState>((set, get) => ({
  byProvider: {},
  register: (provider, items) =>
    set((s) => ({ byProvider: { ...s.byProvider, [provider]: items } })),
  unregister: (provider) =>
    set((s) => {
      const next = { ...s.byProvider }
      delete next[provider]
      return { byProvider: next }
    }),
  all: () => Object.values(get().byProvider).flat(),
}))

/** Page hook — register commands for the current page; auto-cleanup on unmount. */
export function useCommands(provider: string, items: CommandItem[]) {
  const register = useCommandRegistry((s) => s.register)
  const unregister = useCommandRegistry((s) => s.unregister)
  useEffect(() => {
    register(provider, items)
    return () => unregister(provider)
  }, [provider, items, register, unregister])
}

export function CommandPalette() {
  const navigate = useNavigate()
  const open = useUiStore((s) => s.commandPaletteOpen)
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen)
  const setTheme = useUiStore((s) => s.setTheme)
  const { hasPerm, logout } = useAuth()
  // Subscribe to the raw stable map. Flattening into an array MUST happen
  // in useMemo — selecting `Object.values(...).flat()` directly would
  // return a new array each render, fail Zustand's reference-equality
  // check, and infinite-loop via useSyncExternalStore.
  const byProvider = useCommandRegistry((s) => s.byProvider)
  const dynamicCommands = useMemo(() => Object.values(byProvider).flat(), [byProvider])

  // Cmd/Ctrl+K to toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(!open)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  // Static built-ins always available
  const builtIn = useMemo<CommandItem[]>(
    () => [
      { id: 'go.cockpit', label: 'Go to Cockpit', group: 'Navigate', icon: 'grid-2', run: () => navigate('/') },
      { id: 'go.inbox', label: 'Go to Inbox', group: 'Navigate', icon: 'inbox', run: () => navigate('/inbox') },
      { id: 'go.live', label: 'Go to Live visitors', group: 'Navigate', icon: 'wave-pulse', perm: 'crm.read', run: () => navigate('/live') },
      { id: 'go.customers', label: 'Go to Customers', group: 'Navigate', icon: 'users', perm: 'customers.read', run: () => navigate('/customers') },
      { id: 'go.subs', label: 'Go to Subscriptions', group: 'Navigate', icon: 'layer-group', run: () => navigate('/subscriptions') },
      { id: 'go.invoices', label: 'Go to Invoices', group: 'Navigate', icon: 'file-invoice-dollar', perm: 'invoices.read', run: () => navigate('/finance/invoices') },
      { id: 'go.logs', label: 'Go to Logs', group: 'Navigate', icon: 'rectangle-terminal', perm: 'logs.read', run: () => navigate('/ops/logs') },
      { id: 'go.services', label: 'Go to Services', group: 'Navigate', icon: 'server', run: () => navigate('/ops/services') },
      { id: 'go.service-keys', label: 'Go to Service keys', group: 'Navigate', icon: 'key', perm: 'admin.service_keys', run: () => navigate('/admin/service-keys') },

      { id: 'theme.system', label: 'Theme: system', group: 'Preferences', icon: 'display', iconVariant: 'solid', run: () => setTheme('system') },
      { id: 'theme.light', label: 'Theme: light', group: 'Preferences', icon: 'sun', iconVariant: 'solid', run: () => setTheme('light') },
      { id: 'theme.dark', label: 'Theme: dark', group: 'Preferences', icon: 'moon', iconVariant: 'solid', run: () => setTheme('dark') },

      { id: 'session.logout', label: 'Sign out', group: 'Session', icon: 'arrow-right-from-bracket', iconVariant: 'solid', run: () => logout() },
    ],
    [navigate, setTheme, logout],
  )

  const allCommands = useMemo(
    () => [...dynamicCommands, ...builtIn].filter((c) => !c.perm || hasPerm(c.perm)),
    [dynamicCommands, builtIn, hasPerm],
  )

  const grouped = useMemo(() => {
    const map: Record<string, CommandItem[]> = {}
    for (const c of allCommands) {
      const g = c.group ?? 'Actions'
      ;(map[g] ??= []).push(c)
    }
    return Object.entries(map)
  }, [allCommands])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-stretch sm:items-start justify-center sm:p-4 sm:pt-[15vh]"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm hidden sm:block" />
      <Command
        label="Command palette"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative z-10 w-full flex flex-col bg-surface-raised overflow-hidden',
          // Phones: full screen. sm+: the floating palette.
          'h-dvh pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
          'sm:h-auto sm:pt-0 sm:pb-0 sm:max-w-xl sm:rounded-xl sm:border sm:border-hairline sm:shadow-[var(--shadow-popover)]',
        )}
      >
        <div className="flex items-center border-b border-hairline shrink-0">
          <Icon
            name="magnifying-glass"
            variant="solid"
            className="sm:hidden text-sm text-ink-400 pl-4"
          />
          <Command.Input
            autoFocus
            placeholder="Type a command or search…"
            className={cn(
              'flex-1 min-w-0 h-14 sm:h-12 px-3 sm:px-4 bg-transparent text-base sm:text-sm text-ink-900 placeholder:text-ink-400 outline-none focus-visible:shadow-none',
            )}
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="sm:hidden h-11 px-4 text-sm font-medium text-primary cursor-pointer"
          >
            Cancel
          </button>
        </div>
        <Command.List className="flex-1 min-h-0 sm:flex-none sm:max-h-[60vh] overflow-y-auto overscroll-contain clean-scroll p-2">
          <Command.Empty className="px-3 py-8 text-center text-sm text-ink-500">
            No matches.
          </Command.Empty>
          {grouped.map(([group, items]) => (
            <Command.Group
              key={group}
              heading={
                <span className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold px-2">
                  {group}
                </span>
              }
              className="mb-2"
            >
              {items.map((c) => (
                <Command.Item
                  key={c.id}
                  value={`${c.label} ${c.hint ?? ''}`}
                  onSelect={() => {
                    setOpen(false)
                    c.run()
                  }}
                  className="flex items-center gap-3 h-11 sm:h-9 px-2 rounded-md text-sm text-ink-700 cursor-pointer aria-selected:bg-primary-tint aria-selected:text-primary-ink"
                >
                  {c.icon && (
                    <Icon
                      name={c.icon}
                      variant={c.iconVariant ?? 'duotone'}
                      fixedWidth
                      className="text-sm shrink-0"
                    />
                  )}
                  <span className="flex-1 truncate">{c.label}</span>
                  {c.hint && (
                    <span className="text-xs text-ink-400 num">{c.hint}</span>
                  )}
                  {c.shortcut && (
                    <span className="text-[10px] text-ink-400 border border-hairline rounded px-1 py-px num">
                      {c.shortcut}
                    </span>
                  )}
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
        <div className="hidden sm:flex items-center justify-between px-3 py-2 border-t border-hairline text-[10px] text-ink-400">
          <span className="num">↑↓ navigate · ⏎ select · esc close</span>
          <span className="num">{allCommands.length} commands</span>
        </div>
      </Command>
    </div>
  )
}
